/**
 * Role predicates shared by the sidebar and route guards, so nav visibility and route access can
 * never drift apart.
 */

/**
 * A user who may access the educator surfaces: a coach/tutor, a platform admin, or an organization
 * staff/admin member. This is the client-side nav/route predicate - the same one the sidebar has
 * long used as `canViewClassKnowledgeMap`. It is a coarse gate, NOT a full mirror of the server's
 * per-cohort authorization: the authoritative check is `authorizeCohort` in server/lib/cohortAuth.js
 * (which additionally authorizes a class's instructor for that class's cohort). The server enforces
 * that independently on every request; this predicate only decides nav visibility and the
 * client-side route redirect.
 *
 * @param {object|null|undefined} user - the authenticated user (isCoach, isAdmin, organizationMemberships)
 * @returns {boolean}
 */
export function isEducator(user) {
  if (!user) return false
  // The auth payload exposes the memberships as `user.organizations` (see authStore); the
  // `organizationMemberships` name is the server-side Prisma relation and never reaches the client,
  // so reading only that made the org-staff branch dead code. Both are accepted defensively.
  const memberships = user.organizations || user.organizationMemberships || []
  const isOrgStaff = memberships.some((m) => m.role === 'admin' || m.role === 'staff')
  return Boolean(user.isCoach || user.isAdmin || isOrgStaff)
}

/**
 * Whether an organization is an institution - a school or program, as opposed to the two
 * non-institutional org types the platform creates automatically: `personal` (a solo workspace,
 * given to every user) and `family` (a guardian's household).
 *
 * `Organization.type` is a free-form string, not an enum, so this is an exclusion test rather than
 * an allowlist: an org typed `school`, `district` or `academy` all count, while a solo tutor's
 * personal workspace and a guardian's family org do not. Used to gate institution-only surfaces
 * such as the School Cockpit, whose portfolio view is meaningless without a school behind it.
 *
 * @param {{type?: string}|null|undefined} org - an organization from `user.organizations`
 * @returns {boolean}
 */
export function isInstitutionOrg(org) {
  if (!org?.type) return false
  return org.type !== 'personal' && org.type !== 'family'
}
